// inputValidation.js - Utility functions for formatting and validating sensitive input fields
import { validateTaxIdValue, getCountryConfig } from "../../CreatorUser/CreatorPages/Dashboard/editCreator/constants";

/**
 * Format SSN as XXX-XX-XXXX
 */
export const formatSSN = (value) => {
  if (!value) return '';
  
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Limit to 9 digits
  const limited = digits.slice(0, 9);
  
  // Format as XXX-XX-XXXX
  if (limited.length >= 5) {
    return `${limited.slice(0, 3)}-${limited.slice(3, 5)}-${limited.slice(5)}`;
  } else if (limited.length >= 3) {
    return `${limited.slice(0, 3)}-${limited.slice(3)}`;
  }
  
  return limited;
};

/**
 * Format EIN as XX-XXXXXXX
 */
export const formatEIN = (value) => {
  if (!value) return '';
  
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Limit to 9 digits
  const limited = digits.slice(0, 9);
  
  // Format as XX-XXXXXXX
  if (limited.length > 2) {
    return `${limited.slice(0, 2)}-${limited.slice(2)}`;
  }
  
  return limited;
};

/**
 * Format ITIN as XXX-XX-XXXX (same as SSN)
 */
export const formatITIN = formatSSN;

/**
 * Format routing number (9 digits, no formatting)
 */
export const formatRoutingNumber = (value) => {
  if (!value) return '';
  
  // Remove all non-digits and limit to 9
  return value.replace(/\D/g, '').slice(0, 9);
};

/**
 * Format account number (limit to 17 digits per banking standards)
 */
export const formatAccountNumber = (value) => {
  if (!value) return '';
  
  // Remove all non-digits and limit to 17
  return value.replace(/\D/g, '').slice(0, 17);
};

/**
 * Format SWIFT code (8-11 alphanumeric)
 */
export const formatSWIFT = (value) => {
  if (!value) return '';
  
  // Remove spaces and limit to 11 characters
  return value.replace(/\s/g, '').toUpperCase().slice(0, 11);
};

/**
 * Format IBAN (up to 34 alphanumeric)
 */
export const formatIBAN = (value) => {
  if (!value) return '';
  
  // Remove spaces and limit to 34 characters
  return value.replace(/\s/g, '').toUpperCase().slice(0, 34);
};

/**
 * Format postal/ZIP code based on country
 */
export const formatPostalCode = (value, country = 'US') => {
  if (!value) return '';
  
  if (country === 'US') {
    // US ZIP: 5 digits or 5+4
    const digits = value.replace(/\D/g, '');
    const limited = digits.slice(0, 9);
    
    if (limited.length > 5) {
      return `${limited.slice(0, 5)}-${limited.slice(5)}`;
    }
    return limited;
  } else if (country === 'CA') {
    // Canadian postal code: A1A 1A1
    const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    const limited = cleaned.slice(0, 6);
    
    if (limited.length > 3) {
      return `${limited.slice(0, 3)} ${limited.slice(3)}`;
    }
    return limited;
  }
  
  // Other countries: just limit length
  return value.slice(0, 10);
};

/**
 * Validate tax ID based on type and country.
 *
 * Backward compatible: if `country` is omitted, falls back to US-style rules
 * for ssn/ein/itin and a lenient alphanumeric check for anything else so we
 * never hard-block legitimate foreign tax IDs.
 */
export const validateTaxId = (value, type, country) => {
  if (!value) return false;

  // Delegate to the country-aware validator when we have a country code.
  if (country) {
    return validateTaxIdValue(value, type, country);
  }

  const digits = value.replace(/\D/g, '');

  switch (type) {
    case 'ssn':
    case 'ein':
    case 'itin':
      return digits.length === 9;
    case 'foreign_tin':
      return digits.length >= 6;
    default: {
      // Lenient fallback: any 4-30 alphanumeric string.
      const normalized = value.toString().toUpperCase().replace(/[^A-Z0-9]/g, '');
      return normalized.length >= 4 && normalized.length <= 30;
    }
  }
};

/**
 * Validate routing number (must be 9 digits and pass checksum)
 */
export const validateRoutingNumber = (value) => {
  if (!value) return false;
  
  const digits = value.replace(/\D/g, '');
  
  if (digits.length !== 9) return false;
  
  // ABA routing number checksum validation
  const checksum = (
    3 * (parseInt(digits[0]) + parseInt(digits[3]) + parseInt(digits[6])) +
    7 * (parseInt(digits[1]) + parseInt(digits[4]) + parseInt(digits[7])) +
    1 * (parseInt(digits[2]) + parseInt(digits[5]) + parseInt(digits[8]))
  ) % 10;
  
  return checksum === 0;
};

/**
 * Validate account number (4-17 digits)
 */
export const validateAccountNumber = (value) => {
  if (!value) return false;
  
  const digits = value.replace(/\D/g, '');
  return digits.length >= 4 && digits.length <= 17;
};

/**
 * Check if a value is masked (contains asterisks or bullets)
 */
export const isMaskedValue = (value) => {
  if (!value) return false;
  return /[*●]/.test(value);
};

/**
 * Get helper text for tax ID based on type and country.
 *
 * Uses the country-specific placeholder when available so creators see the
 * correct format hint (e.g. PAN for India, CPF for Brazil).
 */
export const getTaxIdHelperText = (type, isValid, value, country) => {
  if (!value) {
    switch (type) {
      case 'ssn':
        return 'Format: XXX-XX-XXXX (9 digits)';
      case 'ein':
        return 'Format: XX-XXXXXXX (9 digits)';
      case 'itin':
        return 'Format: XXX-XX-XXXX (9 digits)';
      case 'foreign_tin':
        return 'Foreign Tax ID (varies by country)';
      default: {
        if (country) {
          const config = getCountryConfig(country);
          if (config?.taxIdPlaceholder) return `Format: ${config.taxIdPlaceholder}`;
        }
        return '';
      }
    }
  }

  if (isValid) {
    return '✓ Valid format';
  }

  return 'Invalid format - please check';
};

/**
 * Get helper text for routing number
 */
export const getRoutingHelperText = (isValid, value) => {
  if (!value) {
    return '9-digit routing number';
  }
  
  if (value.length < 9) {
    return `${value.length}/9 digits`;
  }
  
  if (isValid) {
    return '✓ Valid routing number';
  }
  
  return 'Invalid routing number checksum';
};

/**
 * Get helper text for account number
 */
export const getAccountHelperText = (isValid, value) => {
  if (!value) {
    return '4-17 digit account number';
  }
  
  const digits = value.replace(/\D/g, '');
  
  if (digits.length < 4) {
    return `${digits.length}/4 minimum digits`;
  }
  
  if (isValid) {
    return '✓ Valid account number';
  }
  
  return 'Account number too long (max 17 digits)';
};